import { Close as CloseIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { MuiMarkdown } from "mui-markdown";
import { useEffect, useState } from "react";
import { useArgusSessionReport } from "../../hooks/argus";

// Override default mui-markdown heading sizes — the agent writes each
// section as `### [SECTION] — title @ ts`, and the default h3 rendering
// is too large for a narrow side drawer. Tighten h1-h6 + compact
// paragraphs/lists. Code blocks keep the default styling.
const headingSx = (fontSize: string, mt: number) => ({
  fontSize,
  fontWeight: 600,
  mt,
  mb: 0.75,
  lineHeight: 1.35,
});
const MARKDOWN_OVERRIDES = {
  h1: { component: Typography, props: { sx: headingSx("1.1rem", 2) } },
  h2: { component: Typography, props: { sx: headingSx("1rem", 1.75) } },
  h3: { component: Typography, props: { sx: headingSx("0.95rem", 1.5) } },
  h4: { component: Typography, props: { sx: headingSx("0.9rem", 1.25) } },
  h5: { component: Typography, props: { sx: headingSx("0.875rem", 1) } },
  h6: { component: Typography, props: { sx: headingSx("0.875rem", 1) } },
  p: {
    component: Typography,
    props: {
      variant: "body2",
      sx: { mb: 1, lineHeight: 1.6, whiteSpace: "pre-wrap" },
    },
  },
  li: {
    component: Typography,
    props: { variant: "body2", component: "li", sx: { mb: 0.25 } },
  },
};

interface ArgusReportDrawerProps {
  /** DB session id (not the gateway agent_session_id). */
  sessionId: string | undefined;
  open: boolean;
  onClose: () => void;
  /** Whether the session is still running — drives poll cadence + the
   *  "Live"/"Final" header chip. Callers should pass the session_status
   *  derived `=== "active"` boolean. */
  isLive: boolean;
}

/**
 * Side drawer that renders the agent's `report.md` for a session.
 *
 * The drawer polls `GET /client/argus/sessions/{id}/report` every 5s
 * while the session is live, stopping once the session transitions to
 * a terminal status. Polling is also gated on `open` so closed drawers
 * don't hit the network.
 *
 * Rendering uses `mui-markdown` defaults — deliberately minimal, no
 * Monaco wrapping like ArgusMessageList does. The report is plain prose.
 */
export const ArgusReportDrawer = ({ sessionId, open, onClose, isLive }: ArgusReportDrawerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { reportMd, isLoading, error, refetch } = useArgusSessionReport(sessionId, open, isLive);

  // "Fetched Xs ago" caption — computed from the last successful fetch.
  // We can't use the session's `updated_at` reliably since it reflects
  // the session row, not the report file. Track locally instead: bump
  // the timestamp each time reportMd or isLoading settles from a load.
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Stamp fetchedAt when the isLoading edge falls and the drawer is open.
  useEffect(() => {
    if (!isLoading && open) {
      setFetchedAt(Date.now());
    }
  }, [isLoading, open]);

  // Tick the "Xs ago" caption once a second while open — cheap.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const fetchedAgoLabel = fetchedAt
    ? `Updated ${Math.max(0, Math.round((nowMs - fetchedAt) / 1000))}s ago`
    : "Waiting for first fetch…";

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "min(800px, 85vw)",
          height: isMobile ? "85vh" : "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            Session Report
          </Typography>
          <Chip
            label={isLive ? "Live" : "Final"}
            size="small"
            color={isLive ? "info" : "default"}
            variant={isLive ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
          <Tooltip title="Close" arrow>
            <IconButton size="small" onClick={onClose} aria-label="Close report drawer">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* First-load progress bar — shown only on initial fetch so
          subsequent background refetches don't flash the bar. */}
      {isLoading && reportMd === null && <LinearProgress />}

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: 2,
          py: 2,
        }}
      >
        {error ? (
          <Typography variant="body2" color="error">
            Failed to load report: {error instanceof Error ? error.message : "unknown error"}
          </Typography>
        ) : reportMd ? (
          <MuiMarkdown overrides={MARKDOWN_OVERRIDES}>{reportMd}</MuiMarkdown>
        ) : !isLoading ? (
          <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
            The agent hasn't written a report yet.
          </Typography>
        ) : null}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
            {fetchedAgoLabel}
          </Typography>
          <Tooltip title="Refresh now" arrow>
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={isLoading}
              aria-label="Refresh report"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Drawer>
  );
};
